/**
 * The aircraft's altimeter and barometer calibrations (MB, decision 147; TJR inventory 02 §11; docs/specs/
 * aircraft-altimeter-cals.md). TJR kept them per aircraft in its program file `baro.dat`, edited in its Alt/Baro
 * dialog (`baroFM`): a calibration date and rows of three numbers, the radar altitude, the barometric reading and the
 * GPS height, typed one row per line ("Each line must have 3 numbers seperated by at least one space. Max value is
 * 10,000"). Teslyn keeps them on the aircraft record in Teslyn Cloud (`aircraft.altimeter_cals`), edited by tenant
 * admins, managers and data processors and read by Teslyn Field, and prints them in the final report's appendix
 * "Radar altimeter and barometric altimeter checks" (decision 18). The figures are kept as typed: TJR named no
 * units, and its files hold radar and GPS heights near each other (39.70 and 40.13) with barometric readings in the
 * hundreds or thousands (1010.26, 1375).
 */
import type { Aircraft, IsoDate } from './model.js';

/** One check: the three readings at one height. */
export interface AltimeterCalRow {
  readonly radar_alt: number;
  readonly baro: number;
  readonly gps_ht: number;
}

/** One calibration of an aircraft: its date and its rows. */
export interface AltimeterCal {
  readonly date: IsoDate;
  readonly rows: readonly AltimeterCalRow[];
}

/** TJR's limit on every value (RS 64466). */
export const ALTIMETER_CAL_MAX = 10_000;
/** Rows of one calibration; TJR's memo had no limit, its files hold 10. */
export const ALTIMETER_CAL_MAX_ROWS = 50;
/** Calibrations kept per aircraft. */
export const ALTIMETER_CALS_MAX = 40;

/** Who changes them (decision 147: managers and processors; tenant admins as for every register setting). */
export const ALTIMETER_CAL_ROLES: readonly string[] = ['tenant_admin', 'manager', 'data_processor'];

export function canEditAltimeterCals(roles: readonly string[] | null | undefined): boolean {
  return (roles ?? []).some((r) => ALTIMETER_CAL_ROLES.includes(r));
}

export interface AltimeterCalIssue {
  /** 1-based line of the typed text, or 0 for the date or the list. */
  readonly line: number;
  readonly message: string;
}

/**
 * The typed rows of one calibration, as TJR's dialog takes them: one row per line, three numbers separated by spaces
 * (commas, tabs and TJR's `~` also accepted), each from 0 to 10,000. Blank lines are skipped.
 */
export function parseAltimeterRows(text: string): { rows: AltimeterCalRow[]; issues: AltimeterCalIssue[] } {
  const rows: AltimeterCalRow[] = [];
  const issues: AltimeterCalIssue[] = [];
  text.split(/\r\n|\r|\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (line === '') return;
    const parts = line.split(/[\s,~;]+/).filter((x) => x !== '');
    const nums = parts.map(Number);
    if (parts.length !== 3 || nums.some((n) => !Number.isFinite(n))) {
      issues.push({
        line: i + 1,
        message: `Line ${i + 1}: each line needs three numbers separated by a space.`,
      });
      return;
    }
    if (nums.some((n) => n < 0 || n > ALTIMETER_CAL_MAX)) {
      issues.push({ line: i + 1, message: `Line ${i + 1}: each value is from 0 to 10,000.` });
      return;
    }
    rows.push({ radar_alt: nums[0]!, baro: nums[1]!, gps_ht: nums[2]! });
  });
  if (rows.length === 0 && issues.length === 0) issues.push({ line: 0, message: 'Type at least one row.' });
  if (rows.length > ALTIMETER_CAL_MAX_ROWS)
    issues.push({ line: 0, message: `A calibration holds at most ${ALTIMETER_CAL_MAX_ROWS} rows.` });
  return { rows, issues };
}

/** The rows as the editor shows them: one row per line, the numbers as typed. */
export function formatAltimeterRows(rows: readonly AltimeterCalRow[]): string {
  return rows.map((r) => `${r.radar_alt} ${r.baro} ${r.gps_ht}`).join('\n');
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** The problems of a calibration list as stored (the same rules as the database's `aircraft_altimeter_cals_ok`). */
export function validateAltimeterCals(cals: unknown): string[] {
  if (!Array.isArray(cals)) return ['The calibrations are not a list.'];
  const out: string[] = [];
  if (cals.length > ALTIMETER_CALS_MAX)
    out.push(`An aircraft keeps at most ${ALTIMETER_CALS_MAX} calibrations.`);
  const dates = new Set<string>();
  cals.forEach((c, i) => {
    const cal = c as Partial<AltimeterCal> | null;
    const at = `Calibration ${i + 1}`;
    if (!cal || typeof cal !== 'object') {
      out.push(`${at} is not a calibration.`);
      return;
    }
    if (
      typeof cal.date !== 'string' ||
      !ISO.test(cal.date) ||
      Number.isNaN(Date.parse(`${cal.date}T00:00:00Z`))
    )
      out.push(`${at} has no valid date.`);
    else if (dates.has(cal.date)) out.push(`Two calibrations are dated ${cal.date}.`);
    else dates.add(cal.date);
    if (!Array.isArray(cal.rows) || cal.rows.length === 0 || cal.rows.length > ALTIMETER_CAL_MAX_ROWS)
      out.push(`${at} needs 1 to ${ALTIMETER_CAL_MAX_ROWS} rows.`);
    else
      for (const r of cal.rows) {
        const vals = [r?.radar_alt, r?.baro, r?.gps_ht];
        if (
          vals.some((v) => typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > ALTIMETER_CAL_MAX)
        ) {
          out.push(`${at} has a value outside 0 to 10,000.`);
          break;
        }
      }
  });
  return out;
}

/** An aircraft's calibrations, newest first (none when the record has none or they do not read). */
export function altimeterCals(a: Pick<Aircraft, 'altimeter_cals'>): AltimeterCal[] {
  const v = a.altimeter_cals;
  if (!Array.isArray(v) || validateAltimeterCals(v).length > 0) return [];
  return [...v].sort((x, y) => (x.date < y.date ? 1 : x.date > y.date ? -1 : 0));
}

/**
 * The calibration the final report prints for an aircraft: the latest dated on or before the end of the survey, or
 * when every one is later, the earliest of them. Null when the aircraft has none.
 */
export function altimeterCalFor(a: Pick<Aircraft, 'altimeter_cals'>, endDate: IsoDate): AltimeterCal | null {
  const all = altimeterCals(a);
  return all.find((c) => c.date <= endDate) ?? all[all.length - 1] ?? null;
}

/** Add or replace (by date) one calibration. */
export function withAltimeterCal(cals: readonly AltimeterCal[], cal: AltimeterCal): AltimeterCal[] {
  return [...cals.filter((c) => c.date !== cal.date), cal].sort((x, y) => (x.date < y.date ? 1 : -1));
}

/** Remove the calibration of one date. */
export function withoutAltimeterCal(cals: readonly AltimeterCal[], date: IsoDate): AltimeterCal[] {
  return cals.filter((c) => c.date !== date);
}

/**
 * TJR's `baro.dat` once decoded (XOR 0x55; `NeWe` CR length CR, then CRLF records): `004<registration>`, then
 * `005<dd/mm/yyyy>` and `006<radar>~<baro>~<gps>~` rows. Aircraft with no rows (VH-AQS, VH-HVP) are left out.
 * Used for Thomson's one-off import (decision 136) in `20261029110100_aircraft_altimeter_cals_from_tjr.sql`.
 */
export function parseTjrBaroDat(text: string): { registration: string; cal: AltimeterCal }[] {
  const body = text.replace(/^NeWe?\r\d+\r/, '');
  const out: { registration: string; cal: AltimeterCal }[] = [];
  let current: { registration: string; date: string; rows: AltimeterCalRow[] } | null = null;
  const flush = (): void => {
    if (current && current.rows.length > 0 && current.date)
      out.push({ registration: current.registration, cal: { date: current.date, rows: current.rows } });
  };
  for (const line of body.split(/\r\n/)) {
    const code = line.slice(0, 3);
    const rest = line.slice(3);
    if (code === '004') {
      flush();
      current = { registration: rest.trim().toUpperCase(), date: '', rows: [] };
    } else if (code === '005' && current) {
      const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(rest.trim());
      current.date = m ? `${m[3]}-${m[2]}-${m[1]}` : '';
    } else if (code === '006' && current) {
      const [r, b, g] = rest.split('~').map(Number);
      if ([r, b, g].every((v) => v !== undefined && Number.isFinite(v)))
        current.rows.push({ radar_alt: r!, baro: b!, gps_ht: g! });
    }
  }
  flush();
  return out;
}
